import { atom, useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import { useAtomCallbackMemoized } from '../../../hooks/useAtomCallbackMemoized';
import { DebugMutex } from '../../../video-sdk/core/mutex';
import { visitorAtom } from '../../state';
import { loadTone } from '../pre-session-vibes/loadTone';
import { ToneModuleType } from '../pre-session-vibes/types';
import { ActionType } from './types';

const musicStateAtom = atom<ActionType>(ActionType.PAUSE);

const isToneModuleLoading = atom<boolean>(true);

const FADE_TIME_SECONDS = 2;

const BACKGROUND_MUSIC = '/background-music/background-music.mp3';

type PlayerInstance = InstanceType<ToneModuleType['Player']>;

type MusicPlayersType = {
  currentPlayer: PlayerInstance | null;
  nextPlayer: PlayerInstance | null;
};

let globalPlayers: MusicPlayersType | null = null;

/**
 * Serializes play and pause. Play awaits loading the music before it sets the
 * music state, so without this a pause dispatched mid-load (e.g. on unmount
 * when the session starts) sees the stale PAUSE state, no-ops, and the music
 * starts afterwards with no control left to stop it.
 */
const backgroundMusicMutex = new DebugMutex('backgroundMusicMutex');

export async function initializeGlobalPlayers(ToneModule: ToneModuleType): Promise<MusicPlayersType> {
  /**
   * Initializes and returns the global Tone.Player instances used for background music playback.
   *
   * This function implements a singleton pattern. On its first invocation, it:
   * - Preloads an audio buffer from the specified URL.
   * - Creates two Tone.Player instances:
   *   - `currentPlayer` for the currently playing track.
   *   - `nextPlayer` for the upcoming track, enabling seamless crossfading.
   *
   * Why two players?
   * - To ensure seamless looping, one player starts fading in while the other
   *   is still playing, preventing gaps or sudden silence between loops.
   * - The next track is preloaded and ready to play before the current one ends,
   *   ensuring continuous playback without loading delays.
   *
   */
  if (!globalPlayers) {
    const buffer = await ToneModule.ToneAudioBuffer.fromUrl(BACKGROUND_MUSIC);
    const currentPlayer = new ToneModule.Player({
      url: buffer,
      volume: -20,
      loop: false,
      fadeIn: FADE_TIME_SECONDS,
      fadeOut: FADE_TIME_SECONDS,
    }).toDestination();

    const nextPlayer = new ToneModule.Player({
      url: buffer,
      volume: -20,
      loop: false,
      fadeIn: FADE_TIME_SECONDS,
      fadeOut: FADE_TIME_SECONDS,
    }).toDestination();

    globalPlayers = { currentPlayer, nextPlayer };
  }
  return globalPlayers;
}

export function getNextMusicAction(current: ActionType): ActionType {
  return current === ActionType.PAUSE ? ActionType.PLAY : ActionType.PAUSE;
}

export const useBackgroundMusicPlayback = ({
  toneLoader = loadTone,
  overrides,
}: {
  toneLoader?: () => Promise<ToneModuleType>;
  overrides?: { isPreSessionVibesOpenOverride: boolean };
} = {}) => {
  const [currentMusicState, setMusicState] = useAtom(musicStateAtom);
  const [isToneLoading, setIsToneLoading] = useAtom(isToneModuleLoading);
  const getCurrentMusicState = useAtomCallbackMemoized((get) => get(musicStateAtom), []);
  const getIsPreSessionVibesOpen = useAtomCallbackMemoized((get) => get(visitorAtom).preSessionVibesOpen, []);

  const handlePlay = useCallback(async () => {
    /**
     * Starts background music playback with seamless crossfading between two players.
     *
     * - Initializes playback using two `Tone.Player` instances (current and next).
     * - Uses `scheduleRepeat` to alternate between `current` and `next` players.
     * - Prevents gaps by starting and fading in the next track before the current one ends.
     * - The fade in and fade outs are built into Tone.Player and happen automatically on .start() and .stop()
     *
     *
     * Behavior:
     * - The first track starts immediately.
     * - The next track is scheduled to start before the initial loop ends, ensuring a smooth transition.
     * - The current track is stopped after it's duration is met, allowing for a natural fade-out.
     */

    return backgroundMusicMutex.runExclusive(async () => {
      const ToneModule = await toneLoader();
      if (!ToneModule) return;
      if (getCurrentMusicState() === ActionType.PLAY) return;

      globalPlayers = globalPlayers ?? (await initializeGlobalPlayers(ToneModule));

      let { currentPlayer, nextPlayer } = globalPlayers;
      if (!currentPlayer || !nextPlayer) return;
      // Capture a snapshot of the current and next players from the ref.
      // This ensures we have non-null, stable local variables for swapping,
      // avoiding repeated null checks inside the callback.
      const loopDuration = currentPlayer.buffer?.duration || 0;

      /**
       * earlyStartTimeSeconds is an arbitrarily chosen value.
       * The next iteration of the looped audio starts playing this many seconds
       * prior to the existing audio track to give the effect of a seamless piece of audio
       */

      const earlyStartTimeSeconds = 10;
      const transport = ToneModule.getTransport();

      currentPlayer.start(0);

      transport.scheduleRepeat(
        (time: number) => {
          [currentPlayer, nextPlayer] = [nextPlayer, currentPlayer];

          nextPlayer?.start(time);
          transport.scheduleOnce(() => {
            currentPlayer?.stop();
          }, time + loopDuration);
        },
        loopDuration - earlyStartTimeSeconds,
        '0',
      );

      if (transport.state !== 'started') {
        transport.start();
      }
      setMusicState(ActionType.PLAY);
      setIsToneLoading(false);
    }, 'handlePlay');
  }, [getCurrentMusicState, setMusicState, toneLoader, setIsToneLoading]);

  const handlePause = useCallback(async () => {
    /**
     * Pauses background music playback and stops the looping mechanism.
     *
     * - Stops both `Tone.Player` instances immediately.
     * - Cancels all scheduled transport events to prevent unintended restarts.
     * - Updates the music state to `"Pause"` to reflect the current status.
     *
     * Behavior:
     * - Music playback stops immediately, cutting off the current track.
     * - Any scheduled transitions (such as crossfades or looping) are canceled.
     * - When resumed via `playMusic()`, playback starts from the beginning (not from the paused position).
     *
     */
    return backgroundMusicMutex.runExclusive(async () => {
      const ToneModule = await toneLoader();
      if (!ToneModule) return;
      if (getCurrentMusicState() === ActionType.PAUSE) return;

      globalPlayers = globalPlayers ?? (await initializeGlobalPlayers(ToneModule));
      setIsToneLoading(false);

      const { currentPlayer, nextPlayer } = globalPlayers;
      const transport = ToneModule.getTransport();
      transport.cancel();
      transport.stop();
      currentPlayer?.stop();
      nextPlayer?.stop();
      setMusicState(ActionType.PAUSE);
    }, 'handlePause');
  }, [getCurrentMusicState, setMusicState, toneLoader, setIsToneLoading]);

  useEffect(
    function cleanUpPlayers() {
      return () => {
        const isPreSessionVibesOpen = overrides?.isPreSessionVibesOpenOverride ?? getIsPreSessionVibesOpen();
        if (isPreSessionVibesOpen) return;
        handlePause();
      };
    },
    [getIsPreSessionVibesOpen, handlePause, overrides],
  );

  const toggleMusic = useCallback(() => {
    const nextState = getNextMusicAction(getCurrentMusicState() ?? ActionType.PAUSE);
    nextState === ActionType.PLAY ? handlePlay() : handlePause();
  }, [handlePlay, handlePause, getCurrentMusicState]);

  return {
    toggleMusic,
    handlePlay,
    handlePause,
    isToneLoading,
    currentMusicState,
  };
};
